import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { Link } from "react-router-dom";
import {
  CONTACT,
  IMAGES,
  BOARD_MEMBERS,
  COMMITTEE_LEADS,
} from "../lib/content";

const initials = (name) =>
  name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("");

const PersonCard = ({ name, role }) => (
  <div
    className="card-surface h-full flex-row items-center gap-4 !p-5"
    data-testid="leader-card"
  >
    <span
      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10 font-display text-lg font-extrabold text-primary dark:text-accent"
      aria-hidden="true"
    >
      {initials(name)}
    </span>
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="font-display text-base font-bold leading-snug">{name}</span>
      <span className="text-sm text-muted-foreground">{role}</span>
    </span>
  </div>
);

const VALUES = [
  {
    title: "Who we serve",
    body: "Hawaiʻi residents and the local businesses and families that keep our communities running. Our current emergency program, Priced Out of Kauaʻi by Lowell, serves Hawaiʻi residents who live on Kauaʻi and own and operate locally owned businesses physically located on Kauaʻi.",
  },
  {
    title: "501(c)(3) status",
    body: "Kamalama Foundation is a tax-exempt charitable organization recognized under Section 501(c)(3) of the Internal Revenue Code. Contributions are tax-deductible to the extent permitted by law.",
  },
  {
    title: "Community approach",
    body: "Immediate needs come first. We share verified resources, coordinate volunteers and donated services, and work with community partners before asking anyone for anything. Kauaʻi takes care of Kauaʻi.",
  },
  {
    title: "Transparency and accountability",
    body: "We post a Transparency & Impact Update every Sunday. Any future awards will follow written, Board-approved criteria, be reviewed by an Independent Grant Review Committee, and follow strict conflict-of-interest and recusal rules.",
  },
];

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="About"
        lines={[{ text: "Kamalama" }, { text: "Foundation", className: "font-serifg italic font-semibold" }]}
        lead="Kamalama Foundation strengthens Hawaiʻi communities through charitable support for local residents, community programs, locally owned businesses, and educational opportunities."
      />
      <section className="wrap py-16 sm:py-20" data-testid="about-values">
        <div className="grid gap-5 md:grid-cols-2">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.06} className="h-full">
              <div className="card-surface h-full">
                <h2 className="font-display text-2xl font-bold">{v.title}</h2>
                <p className="leading-relaxed text-muted-foreground">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="border-t border-border">
        <div className="wrap flex flex-col gap-10 py-16 sm:py-20">
          <Reveal className="flex flex-col gap-4">
            <p className="eyebrow">Leadership</p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Board & Committee Leadership
            </h2>
            <p className="max-w-3xl leading-relaxed text-muted-foreground">
              The people who keep Kamalama accountable — the Board of
              Directors and the committee leads who run our programs day to
              day. Photographs and short biographies will be added as they
              become available.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h3 className="font-display text-xl font-bold">Board of Directors</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="board-grid">
              {BOARD_MEMBERS.map((m) => (
                <PersonCard key={m.name} name={m.name} role={m.role} />
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="font-display text-xl font-bold">Committee Leadership</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="committee-grid">
              {COMMITTEE_LEADS.map((m) => (
                <PersonCard key={m.name} name={m.name} role={m.role} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="wrap grid items-center gap-10 py-16 sm:py-20 lg:grid-cols-2">
          <Reveal className="overflow-hidden rounded-3xl">
            <img
              src={IMAGES.communityOutdoors}
              alt="Kauaʻi community members together outdoors"
              className="h-80 w-full object-cover transition duration-700 hover:scale-105"
              data-testid="about-image"
            />
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col items-start gap-4">
            <p className="eyebrow">Talk story</p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Questions? Come talk story with us.
            </h2>
            <p className="leading-relaxed text-muted-foreground">
              {CONTACT.email} · {CONTACT.phone} · {CONTACT.address}
            </p>
            <Link to="/contact" className="btn" data-testid="about-contact-cta">
              Send us a message
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
